import { DeployEnv } from './types';
import {
  findBackendTarget,
  findBackendTargetByDeployment,
  findBackendTargetByIdentity,
  getGalileoEnvironment,
  resolveAuthProfile,
} from './profiles';
import type { BackendTargetProfile } from './profiles';
import {
  normalizeApiHost,
  hasRuntimeSourceValue,
  parseDeployEnv,
  parsePlatform,
  readRuntimeSources,
  type RuntimeSourceInput,
} from './sources';
import type {
  BizPlatform,
  RuntimeConfigSource,
  RuntimeContext,
  RuntimeResolveIssue,
  RuntimeResolveResult,
} from './types';

export * from './profiles';
export * from './sources';
export * from './types';

const isLocalHostname = (hostname: string): boolean =>
  /^(localhost|127\.0\.0\.1|\d{1,3}(?:\.\d{1,3}){3})$/.test(hostname);

const parseBoolean = (value: unknown): boolean => value === true || value === 'true';

const FIELD_ORDER = ['apiHost', 'platform', 'stage'] as const;

interface DeploymentSelection {
  readonly source: RuntimeConfigSource;
  readonly apiHost: unknown;
  readonly platform: unknown;
  readonly stage: unknown;
}

interface ResolvedBackend {
  readonly profile: BackendTargetProfile;
  readonly overrideApplied: boolean;
}

const fallbackProfile = (): BackendTargetProfile => {
  const profile = findBackendTarget('gcp_prod');
  if (!profile) throw new Error('Missing required gcp_prod runtime profile');
  return profile;
};

function deepFreeze<T>(value: T): T {
  if (typeof value !== 'object' || value === null || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

function selectSourceValue(serverValue: unknown, viteValue: unknown): { value: unknown; source: RuntimeConfigSource } {
  if (hasRuntimeSourceValue(serverValue)) return { value: serverValue, source: 'server' };
  return { value: viteValue, source: 'vite' };
}

function selectDeploymentValues(input: RuntimeSourceInput): DeploymentSelection {
  const apiHost = selectSourceValue(input.server.apiHost, input.vite.apiHost);
  const platform = selectSourceValue(input.server.platform, input.vite.platform);
  const stage = selectSourceValue(input.server.stage, input.vite.stage);
  return {
    source:
      apiHost.source === 'server' || platform.source === 'server' || stage.source === 'server' ? 'server' : 'vite',
    apiHost: apiHost.value,
    platform: platform.value,
    stage: stage.value,
  };
}

function fieldTokens(flags: { apiHost: boolean; platform: boolean; stage: boolean }): string {
  return FIELD_ORDER.filter((field) => flags[field]).join(',');
}

function identityIssue(
  invalid: { apiHost: boolean; platform: boolean; stage: boolean },
  missing: { apiHost: boolean; platform: boolean; stage: boolean },
): RuntimeResolveIssue {
  const detail = fieldTokens({
    apiHost: invalid.apiHost || missing.apiHost,
    platform: invalid.platform || missing.platform,
    stage: invalid.stage || missing.stage,
  });
  const code = invalid.apiHost || invalid.platform || invalid.stage ? 'CONFIG_INVALID' : 'CONFIG_MISSING';
  return detail ? { code, detail } : { code };
}

function localProfile(platform: BizPlatform, apiHost: string): BackendTargetProfile {
  return { targetId: 'local', platform, stage: DeployEnv.LOCAL, apiHost };
}

function resolveDeployment(input: RuntimeSourceInput) {
  const selection = selectDeploymentValues(input);
  const platform = parsePlatform(selection.platform);
  const stage = parseDeployEnv(selection.stage);
  const apiHost = normalizeApiHost(selection.apiHost);
  const missing = {
    apiHost: !hasRuntimeSourceValue(selection.apiHost),
    platform: !hasRuntimeSourceValue(selection.platform),
    stage: !hasRuntimeSourceValue(selection.stage),
  };
  const invalid = {
    apiHost: !missing.apiHost && !apiHost,
    platform: !missing.platform && !platform,
    stage: !missing.stage && !stage,
  };
  const { source } = selection;

  if (!platform || !stage) {
    const profile = fallbackProfile();
    return {
      deployment: {
        source,
        apiHost: profile.apiHost,
        platform: profile.platform,
        stage: profile.stage,
      },
      backendProfile: profile,
      parsedStage: stage,
      issues: [identityIssue(invalid, missing)],
    };
  }

  if (stage === DeployEnv.LOCAL) {
    // 本地没有正式 profile，host 不合法时也不能改去生产。
    const host = apiHost || 'localhost';
    return {
      deployment: { source, apiHost: host, platform, stage },
      backendProfile: localProfile(platform, host),
      parsedStage: stage,
      issues: apiHost ? [] : [identityIssue(invalid, missing)],
    };
  }

  if (apiHost) {
    const matched = findBackendTargetByIdentity(platform, stage, apiHost);
    if (matched) {
      return {
        deployment: { source, apiHost, platform, stage },
        backendProfile: matched,
        parsedStage: stage,
        issues: [],
      };
    }
  }

  const canonical = findBackendTargetByDeployment(platform, stage);
  if (canonical) {
    const issue: RuntimeResolveIssue = apiHost
      ? { code: 'TARGET_UNKNOWN', detail: `${platform}_${stage}` }
      : identityIssue(invalid, missing);
    return {
      deployment: { source, apiHost: canonical.apiHost, platform, stage },
      backendProfile: canonical,
      parsedStage: stage,
      issues: [issue],
    };
  }

  // 没有该身份的正式 profile 时，只把请求目标落到 gcp_prod。
  const profile = fallbackProfile();
  const issue: RuntimeResolveIssue = apiHost
    ? { code: 'TARGET_UNKNOWN', detail: `${platform}_${stage}` }
    : identityIssue(invalid, missing);
  return {
    deployment: { source, apiHost: apiHost || profile.apiHost, platform, stage },
    backendProfile: profile,
    parsedStage: stage,
    issues: [issue],
  };
}

function resolveTransport(input: RuntimeSourceInput): { transport: RuntimeContext['transport']; direct: boolean } {
  const proxyMode = Boolean(input.vite.isDev && input.proxyPrefix && isLocalHostname(input.location.hostname));
  if (!proxyMode) return { transport: {}, direct: true };
  return {
    transport: { proxyBaseUrl: `${input.location.host}${input.proxyPrefix}` },
    direct: false,
  };
}

function applyTargetOverride(
  input: RuntimeSourceInput,
  profile: BackendTargetProfile,
  allowed: boolean,
): ResolvedBackend {
  if (!input.targetOverride || !allowed) return { profile, overrideApplied: false };
  const overrideTarget = findBackendTarget(input.targetOverride);
  if (!overrideTarget) return { profile, overrideApplied: false };
  return { profile: overrideTarget, overrideApplied: true };
}

function shouldDiscardOverride(
  input: RuntimeSourceInput,
  direct: boolean,
  parsedStage: DeployEnv | undefined,
): boolean {
  if (!input.targetOverride) return false;
  // pre/prod 没有 DevTool，残留 Cookie 直接清掉，不看目标是否合法。
  if (parsedStage === DeployEnv.PRE || parsedStage === DeployEnv.PROD) return true;
  return !findBackendTarget(input.targetOverride) || !direct;
}

export function resolveRuntimeContext(input: RuntimeSourceInput = readRuntimeSources()): RuntimeResolveResult {
  // 兜底会改写 deployment。监控和清 Cookie 都看解析前的阶段。
  const { deployment, backendProfile, parsedStage, issues } = resolveDeployment(input);
  const { transport, direct } = resolveTransport(input);
  const devToolVisible = direct && (deployment.stage === DeployEnv.LOCAL || deployment.stage === DeployEnv.TEST);
  const backend = applyTargetOverride(input, backendProfile, devToolVisible);
  const context: RuntimeContext = {
    deployment,
    backend: {
      targetId: backend.profile.targetId,
      platform: backend.profile.platform,
      stage: backend.profile.stage,
      apiHost: backend.profile.apiHost,
      overrideApplied: backend.overrideApplied,
    },
    transport,
    auth: resolveAuthProfile(deployment.platform, deployment.stage),
    observability: {
      enableProductionTracking: parsedStage === DeployEnv.PROD,
      galileoEnvironment: parsedStage ? getGalileoEnvironment(parsedStage) : 'test',
      galileoTraceEnabled: parseBoolean(input.vite.galileoTraceEnabled),
      isDevelopmentBuild: input.vite.isDev,
    },
    capabilities: { devToolVisible },
  };
  return {
    context: deepFreeze(context),
    issues,
    discardOverride: shouldDiscardOverride(input, direct, parsedStage),
  };
}
